import { createSlice } from "@reduxjs/toolkit";
import { partyApi } from "../../api/partyAPi";
import { withRecordMedia } from "../../utils/recordMedia";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import type { AppDispatch, RootState } from "../../app/store";
import type { PartyItem } from "../../types/party.types";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

export interface PartySearchPayload {
  /** Also list soft-deleted records (admins' "Show Deleted"). */
  includeDeleted?: boolean;
  companyId?: string;
  roleId?: string;
  isAppAdmin?: boolean;
  pageSize?: number;
  pageIndex?: number;
  orderBy?: string;
  sortAscending?: boolean;
  includeFacets?: boolean;
  groupIds?: string[];
  ids?: string[];
  currentProfileId?: string;
  startDate?: string;
  endDate?: string;
  entityTypeNames?: string[];
  partyTypes?: number[];
  registeredNames?: string[];
  emails?: string[];
  isActive?: boolean | null;
  myMode?: boolean;
  searchText?: string;
}

// The newest first-page request (see fetchParties).
let latestPartiesPage = 0;

export const fetchParties = (payload: PartySearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().party;
    const userData = await getCurrentUser();
    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    // A first page always runs (a new search must not be dropped because an
    // earlier one is still loading); an answer to an older one is ignored.
    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage ? ++latestPartiesPage : latestPartiesPage;
    const superseded = () => ticket !== latestPartiesPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        // "newest" maps to creationDate on the backend, so this listing was
        // ordered by when a record was created rather than when it was last
        // touched. Listings are work queues: most recently updated first.
        orderBy: payload.orderBy || "modificationdate",
        sortAscending: payload.sortAscending ?? false,
        includeFacets: payload.includeFacets ?? false,
        groupIds: [`${(await userData).companyId}`],
        ids: payload.ids || [],
        // Admins' "Show Deleted" filter.
        includeDeleted: payload.includeDeleted ?? false,
        entityTypeNames: payload.entityTypeNames || [],
        partyTypes: payload.partyTypes || [],
        registeredNames: payload.registeredNames || [],
        emails: payload.emails || [],
        isActive: payload.isActive ?? null,
        myMode: payload.myMode ?? true,
        searchText: payload.searchText || "",
        // Created-on range from the filter panel's date filter.
        ...(payload.startDate && {
          startDate: `${payload.startDate}T00:00:00`,
        }),
        ...(payload.endDate && { endDate: `${payload.endDate}T23:59:59` }),
      };

      const response = await partyApi.searchParty(requestBody);
      const rawData: PartyItem[] = response.data || [];
      // A party's photo is stored as an object-store id; the listing and the
      // detail page need a link. Resolved in one request for the whole page
      // (the media endpoint is the organisation's, not member-specific).
      const fetchedData = await withRecordMedia(rawData);
      const totalCount = response.count || 0;

      if (superseded()) return;
      dispatch(setTotalParties(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (superseded()) return;
      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setParties(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendParties(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }

      return fetchedData;
    } catch {
      toast.error(
        i18n.t("party:sliceMessages.fetchError", {
          defaultValue: "Failed to fetch parties",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const addNewParty = (formData: Partial<PartyItem>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await partyApi.createParty(formData);
      const newParty = { ...formData, id: res, isActive: true } as PartyItem;
      const { items } = getState().party;
      dispatch(setParties([newParty, ...items]));

      toast.success(
        i18n.t("party:sliceMessages.addSuccess", {
          defaultValue: "Party added successfully",
        }),
      );
    } catch (err) {
      // Show the server's reason (e.g. "A party named … already exists in
      // this organisation") and rethrow so the modal stays open with it.
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("party:sliceMessages.addError", {
            defaultValue: "Failed to add party",
          }),
        ),
      );
      throw err;
    }
  };
};

export const editParty = (formData: Partial<PartyItem>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await partyApi.updateParty(formData);

      const { items } = getState().party;
      const updatedList = items.map((item: PartyItem) =>
        item.id === formData.id ? { ...item, ...formData } : item,
      );

      dispatch(setParties(updatedList));

      toast.success(
        i18n.t("party:sliceMessages.editSuccess", {
          defaultValue: "Party details updated successfully",
        }),
      );
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("party:sliceMessages.editError", {
            defaultValue: "Failed to update party details",
          }),
        ),
      );
      console.error("Update Error:", err);
      throw err;
    }
  };
};

export const removeParty = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await partyApi.removeParty(id);

      const { items } = getState().party;
      const filteredList = items.filter((item: PartyItem) => item.id !== id);

      dispatch(setParties(filteredList));

      toast.success(
        i18n.t("party:sliceMessages.removeSuccess", {
          defaultValue: "Party removed successfully",
        }),
      );
    } catch (err: any) {
      // The API refuses deletion of a party that active contracts still
      // name, and says which ones — show that rather than a generic failure.
      const fallback = i18n.t("party:sliceMessages.removeError", {
        defaultValue: "Failed to remove party",
      });
      reportDeleteFailure(err, fallback);
      console.error("Error deleting party:", err);
      throw err;
    }
  };
};

export const handleActivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await partyApi.activateParty(id);

      const { items } = getState().party;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: true } : item,
      );

      dispatch(setParties(updatedList));

      toast.success(
        i18n.t("party:sliceMessages.statusSuccess", {
          defaultValue: "Party status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("party:sliceMessages.statusError", {
          defaultValue: "Failed to update party status",
        }),
      );
    }
  };
};

export const handleDeactivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await partyApi.deActivateParty(id);

      const { items } = getState().party;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: false } : item,
      );

      dispatch(setParties(updatedList));

      toast.success(
        i18n.t("party:sliceMessages.statusSuccess", {
          defaultValue: "Party status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("party:sliceMessages.statusError", {
          defaultValue: "Failed to update party status",
        }),
      );
    }
  };
};

interface PartyState {
  items: PartyItem[];
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  totalParties: number;
}

const initialState: PartyState = {
  items: [],
  isLoading: false,
  pageIndex: 0,
  hasMore: true,
  totalParties: 0,
};

const partySlice = createSlice({
  name: "party",
  initialState,
  reducers: {
    setParties: (state, action) => {
      state.items = action.payload;
    },
    appendParties: (state, action) => {
      state.items = [...state.items, ...action.payload];
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setTotalParties: (state, action) => {
      state.totalParties = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    resetPartyList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
  },
});

export const {
  setParties,
  appendParties,
  setPageIndex,
  setHasMore,
  resetPartyList,
  setLoading,
  setTotalParties,
} = partySlice.actions;

export default partySlice.reducer;
